import * as React from "react";
import { Search, PanelLeft } from "lucide-react";
import { Button } from "./button.js";
import { useAppearance } from "./appearance-provider.js";
import { cn } from "../lib/cn.js";

/**
 * Topbar primitives — collapse-sidebar button + global search with ⌘K
 * shortcut hint + slot for trailing actions (theme switcher, customizer,
 * user menu). Mirrors the layout in every reference screenshot.
 */

export function Topbar({ className, children, ...props }: React.HTMLAttributes<HTMLElement>) {
  return (
    <header
      className={cn(
        "border-border bg-background/80 sticky top-[var(--app-top-inset,0px)] z-20 flex h-14 shrink-0 items-center gap-2 border-b px-4 backdrop-blur",
        className,
      )}
      {...props}
    >
      {children}
    </header>
  );
}

export function SidebarToggle({ className, ...props }: React.ComponentProps<typeof Button>) {
  const { sidebar, set } = useAppearance();
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={sidebar === "icon" ? "Expand sidebar" : "Collapse sidebar"}
      onClick={() => set({ sidebar: sidebar === "icon" ? "default" : "icon" })}
      className={cn("text-muted-foreground hover:text-foreground", className)}
      {...props}
    >
      <PanelLeft aria-hidden="true" />
    </Button>
  );
}

export interface TopbarSearchProps {
  /** Called when ⌘K / Ctrl-K is pressed anywhere on the page. */
  onActivate?: () => void;
  placeholder?: string;
}

/**
 * Pure presentational search field — clicking or pressing the global
 * shortcut should open a CommandDialog (provided separately by the
 * consuming app). Keeping the dialog out of the topbar means apps can
 * mount their own command source (capabilities, navigation, etc).
 */
export function TopbarSearch({ onActivate, placeholder = "Search…" }: TopbarSearchProps) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onActivate?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onActivate]);

  return (
    <button
      type="button"
      onClick={onActivate}
      className={cn(
        "border-border bg-muted/40 text-muted-foreground group flex h-9 w-full max-w-md items-center gap-2 rounded-md border px-3 text-sm transition-colors",
        "hover:bg-muted hover:text-foreground focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2",
      )}
    >
      <Search className="size-4 shrink-0" aria-hidden="true" />
      <span className="flex-1 truncate text-left">{placeholder}</span>
      <kbd className="border-border bg-card text-muted-foreground hidden items-center gap-0.5 rounded border px-1.5 py-0.5 font-mono text-[10px] font-medium sm:inline-flex">
        <span className="text-xs">⌘</span>K
      </kbd>
    </button>
  );
}

export function TopbarSpacer() {
  return <div className="flex-1" />;
}

export function TopbarActions({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex items-center gap-1", className)} {...props}>
      {children}
    </div>
  );
}
